// What Sayer is told about the chat outside the current forecast, rendered as a transcript
// grouped by question, newest kept first within a size budget. The lines are the ones the server
// chose for the message (POST /api/chat/memory), or this browser's chat journal
// (lib/chat/chatJournal.js) when the server has none. The current forecast's own thread is
// excluded because the prompt already carries it (conversationMemory.js).
//
// Pure: no React, no globals.

/** The most characters of earlier conversation sent with one message (about 3k tokens). */
export const EARLIER_MAX_CHARS = 12000;
/** One earlier line is cut to this length. */
export const EARLIER_LINE_MAX_CHARS = 600;

const clip = (s, max) => {
  const t = String(s ?? "")
    .replace(/\s+/g, " ")
    .trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

const speaker = (role) => (role === "user" ? "User" : "Sayer");

/**
 * The prompt section for the earlier conversation, or "" when there is none. Shared by the
 * forecast and no-forecast chat prompts.
 * @param {string|null|undefined} text from earlierConversationText
 * @returns {string}
 */
export function earlierConversationClause(text) {
  if (!text) return "";
  return `EARLIER CONVERSATION ON OTHER QUESTIONS (this user's chat history, oldest first; "current forecast" is that question's latest answer). Use it when the user refers back to something discussed there or asks you to compare; don't bring it up unprompted, and don't state figures it doesn't contain:
${text}

`;
}

/**
 * The earlier conversation as prompt text, or "" when there is none.
 *
 * @param {object[]} journal the chat journal
 * @param {{
 *   excludeForecastClientId?: string|null,  // the current forecast, already in the prompt
 *   describe?: (forecastClientId: string) => string|null,  // its current answer, one line
 *   maxChars?: number,
 *   lineMaxChars?: number,
 * }} [opts]
 * @returns {string}
 */
export function earlierConversationText(
  journal,
  {
    excludeForecastClientId = null,
    describe = null,
    maxChars = EARLIER_MAX_CHARS,
    lineMaxChars = EARLIER_LINE_MAX_CHARS,
  } = {},
) {
  if (!Array.isArray(journal) || journal.length === 0) return "";
  const msgs = journal.filter(
    (m) =>
      m?.kind === "message" &&
      (m.role === "user" || m.role === "assistant") &&
      m.text &&
      (excludeForecastClientId == null || m.forecastClientId !== excludeForecastClientId),
  );
  if (!msgs.length) return "";
  // Newest first until the budget is spent, then back into reading order.
  const kept = [];
  let used = 0;
  for (let i = msgs.length - 1; i >= 0; i--) {
    const line = `${speaker(msgs[i].role)}: ${clip(msgs[i].text, lineMaxChars)}`;
    if (used + line.length + 1 > maxChars) break;
    kept.push({ m: msgs[i], line });
    used += line.length + 1;
  }
  kept.reverse();
  // Grouped by question, a heading wherever the question changes.
  const out = [];
  let key;
  for (const { m, line } of kept) {
    const k = m.forecastClientId ?? null;
    if (k !== key) {
      key = k;
      if (k == null) out.push("Before a question was asked:");
      else {
        const now = describe?.(k);
        const title = m.forecastTitle ? `"${clip(m.forecastTitle, 160)}"` : "another question";
        out.push(`About ${title}${now ? ` (current forecast: ${now})` : ""}:`);
      }
    }
    out.push(line);
  }
  return out.join("\n");
}
